/**
 * Shared utility functions for review components
 */

/**
 * Extract IDs (prosetId, soalId) from question object
 * Used by ReportSoalButton across all review components
 */
export function extractQuestionIds(question) {
  const originalQuestion = question?.originalQuestion || question;
  return {
    prosetId: originalQuestion?.['proset-id'] || question?.['proset-id'] || question?.prosetId || question?.proset_id || null,
    soalId: originalQuestion?.['soal-id'] || question?.['soal-id'] || question?.soalId || question?.id || null,
  };
}

/**
 * Transform choices from backend format to component options format
 * Used by SingleCorrectAnswer, Passage, MultipleCorrectAnswer
 */
export function transformChoicesToOptions(choices) {
  if (!choices || !Array.isArray(choices)) {
    return [];
  }

  return choices.map((choice) => ({
    id: choice.idx !== undefined ? choice.idx : choice.id,
    text: choice['option-text'] || choice.text || '',
    isCorrect: choice.correct === true,
  }));
}

/**
 * Normalize option ID for comparison (handles both number and string)
 */
export function normalizeOptionId(id) {
  return typeof id === 'number' ? id : Number(id);
}

/**
 * Normalize user answer ID from various formats (array, object, number, string)
 * Used by SingleCorrectAnswer and Passage
 */
export function normalizeUserAnswerId(userAnswer) {
  if (userAnswer === null || userAnswer === undefined) {
    return null;
  }
  
  // If it's an array, take the first element
  if (Array.isArray(userAnswer)) {
    if (userAnswer.length === 0) {
      return null;
    }
    const firstAnswer = userAnswer[0];
    
    // If first element is an object, extract idx/id
    if (typeof firstAnswer === 'object' && firstAnswer !== null) {
      if (firstAnswer.idx !== undefined) {
        return Number(firstAnswer.idx);
      }
      if (firstAnswer.id !== undefined) {
        return Number(firstAnswer.id);
      }
      return null;
    }
    
    const numId = typeof firstAnswer === 'number' ? firstAnswer : Number(firstAnswer);
    return !isNaN(numId) ? numId : null;
  }
  
  // If it's an object with idx/id property
  if (typeof userAnswer === 'object' && userAnswer !== null) {
    if (userAnswer.idx !== undefined) {
      return Number(userAnswer.idx);
    }
    if (userAnswer.id !== undefined) {
      return Number(userAnswer.id);
    }
    // Check if it has 'user-answer' or 'answer' property
    if (userAnswer['user-answer'] !== undefined) {
      const result = Number(userAnswer['user-answer']);
      return !isNaN(result) ? result : null;
    }
    if (userAnswer.answer !== undefined) {
      const result = Number(userAnswer.answer);
      return !isNaN(result) ? result : null;
    }
    return null;
  }
  
  // If it's a number or string that can be converted to number
  const numId = typeof userAnswer === 'number' ? userAnswer : Number(userAnswer);
  return !isNaN(numId) ? numId : null;
}

